/**
 * The real side effects of the dive: the loader and twin stores, the globe
 * engine and twin handle singletons, the router, the shared clock, timers and
 * toasts. The buttons build these with the router's navigate function.
 */
import { toast } from '@/components/ui/toast';
import { getGlobeEngine } from '@/features/loader/globe/engineHandle';
import { useLoader } from '@/features/loader/store';
import { usePass } from '@/features/loader/passes/store';
import { registerAssetSpec } from '@/features/twin/data';
import { getTwinHandle } from '@/features/twin/scene/TwinHandle';
import { useTwin } from '@/features/twin/store';
import { clockNow, useUI } from '@/state/store';
import type { DiveDeps } from './controller';

/** True when the OS asks for reduced motion. */
export function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function createDiveDeps(navigate: (path: string) => void): DiveDeps {
  return {
    loader: () => {
      const s = useLoader.getState();
      return {
        dataset: s.dataset,
        inspectedPass: usePass.getState().selected,
        activeContacts: s.activeContacts,
        contactWindows: s.results?.contactWindows ?? null,
        sceneMode: s.sceneMode,
        google3D: s.google3D,
      };
    },
    setGlobeView: (patch) => useLoader.getState().setView(patch),
    stopTracking: () => {
      if (useLoader.getState().trackedMissionId) useLoader.getState().setTracked(null);
    },
    globe: () => getGlobeEngine(),
    // LoaderGlobe mounts the engine with ionAssets on; the ion token decides whether they load.
    ionAssets: () => Boolean(import.meta.env.VITE_CESIUM_ION_TOKEN),
    currentPath: () => window.location.pathname + window.location.search,
    navigate,
    select: (partial) => useUI.getState().select(partial),
    twin: () => {
      const t = useTwin.getState();
      return {
        assetId: t.assetId,
        loadStatus: t.loadStatus,
        loadError: t.loadError,
        subviewId: t.subviewId,
        headingDeg: t.asset?.site?.headingDeg,
      };
    },
    closeSubview: () => useTwin.getState().setSubview(null),
    twinHandle: () => getTwinHandle(),
    setPointing: (pointing) => useTwin.getState().setPointing(pointing),
    registerAssetSpec: (assetId, spec) => {
      registerAssetSpec(assetId, spec);
    },
    now: () => clockNow(),
    monotonicMs: () => performance.now(),
    sleep: (ms) => new Promise<void>((resolve) => window.setTimeout(resolve, ms)),
    nextFrame: () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve())),
    every: (ms, fn) => {
      const id = window.setInterval(fn, ms);
      return () => window.clearInterval(id);
    },
    reducedMotion: prefersReducedMotion,
    toast,
  };
}
